"use client";

import React, { useState } from "react";
import clsx from "clsx";
import MoreButton from "./MoreButton";

interface WhitePaperItem {
  title: string;
  date: string;
  content: string;
  link: string;
}

/** The four papers are served from this site, out of public/assets/pdf.
 *
 *  They used to be linked absolutely, at https://e-cq.net/assets/pdf/<file>,
 *  which is where v1 kept them. That path died with v1: e-cq.net now serves
 *  this app, whose public/ had no PDFs at all, so every "MORE" button was a
 *  404 — the domain answered, the file did not.
 *
 *  The files are copied in under the same /assets/pdf/<file> path v1 used, so
 *  any inbound link or indexed PDF URL still resolves. The hrefs are relative
 *  rather than absolute so they also work on localhost and staging, instead of
 *  bouncing a reader to production mid-session. */
const WhitePaper = () => {
  // Accordion: track collapsed cards (empty = all open, matching the design).
  const [closedIndices, setClosedIndices] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setClosedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const data: WhitePaperItem[] = [
    {
      title: "Scanning :: Encored",
      date: "04 Jan 2016",
      content:
        "This chapter covers all facets of Scanning, the second phase of a hacking process where hackers will be using aggressive network probing techniques and tools to uncover further information about the target organization. In order to obtain and uncover all potential information related to a particular network, the hackers are required to employ many different scanning types, such as war-dialing, port scanning, OS fingerprinting, vulnerability scanning, and so forth. This paper will discuss these crucial aspects of scanning in details to help you have a better understanding about the techniques taken by the hackers prior to the Penetrating phase.",
      link: "/assets/pdf/scanning-encored.pdf",
    },
    {
      title: "Footprinting :: Encored",
      date: "04 Jan 2016",
      content:
        "This paper introduces you to one of the most important aspects of a premeditated hacking process, footprinting. Footprinting is so essential and important that not so many hackers can possibly complete their journey without first going through this stage. Having a good understanding of footprinting allows you to dig further into the art of information gathering. Later in the paper, you will be introduced to a wide range of methodology and tools that can be used to obtain general information about your target.",
      link: "/assets/pdf/footprinting-encored.pdf",
    },
    {
      title: "Computer Crimes & Legal Implications",
      date: "04 Jan 2016",
      content:
        "Crimes are now no longer restricted by physical security mechanisms when assisted by high-tech devices or computers with Internet connection. The complication and unstoppable growth of computer crimes are the reasons that lead Congress to constitute several computer crime laws to address computer-related criminal activity. One of which is 18 U.S.C . 1029 (Section 1029 of Title 18 of the United States Code), addressing fraud and related activity in connection with access devices. The primary federal statute that can be used to address fraud and related criminal activity in connection with computers is 18 U.S.C . 1030 (Section 1030 of Title 18 of the United States Code). The main goal of this paper is to help you understand these law systems better and how it may help the law enforcement officials punish the wrong doers.",
      link: "/assets/pdf/ccrimes.pdf",
    },
    {
      title: "Instant Messaging technology. Do the advantages outweigh the risks?",
      date: "04 Jan 2016",
      content:
        "Enterprises start to see Instant Messaging (IM) technology as the next revolution, and some even deploy the technology for business use but not many of them knowing that IM applications are facing a lot of security and privacy issues. This paper takes us inside two of the most common security vulnerabilities to date: Buffer Overflow and Cross-Site Scripting (XSS). We'll then start to examine three most popular IM applications: Yahoo! Instant Messenger (YIM), AOL Instant Messenger (AIM) and Microsoft Instant Messenger (MSN Messenger) to see how they are left vulnerable to those security issues.",
      link: "/assets/pdf/im-risk.pdf",
    },
  ];

  return (
    <section className="relative w-full pt-2 pb-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-[1300px] mx-auto flex flex-col gap-12 relative z-10">
        {data.map((item, index) => {
          const isOpen = !closedIndices.has(index);
          return (
            <div key={index} className="relative">
              {/* Header (click to expand/collapse) */}
              <button
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
                className="w-full cursor-pointer rounded-lg bg-gradient-to-r from-[#1f2428] to-[#15181d] p-8 flex items-center gap-8 text-left"
              >
                {/* Chevron: down when open, right when collapsed */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className={clsx(
                    "flex-shrink-0 text-primary transition-transform duration-300",
                    isOpen ? "rotate-0" : "-rotate-90"
                  )}
                  aria-hidden="true"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <div className="flex flex-col items-start gap-1 min-w-0">
                  {/* Title */}
                  <h3 className="text-primary text-[24px] leading-tight font-semibold">
                    {item.title}
                  </h3>
                  {/* Date */}
                  <p className="text-neutral-300 text-xs tracking-[0.4px]">{item.date}</p>
                </div>
              </button>

              {/* Collapsible content */}
              <div
                className={clsx(
                  "grid transition-[grid-template-rows] duration-300 ease-out",
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                )}
              >
                <div className="overflow-hidden">
                  <div className="pl-12 pt-6 flex flex-col gap-4">
                    <p className="body-1 text-white">{item.content}</p>

                    {/* "More" button — ported from v1 (.resources-white-paper__button),
                          opens the PDF in a new tab. */}
                    <MoreButton href={item.link} target="_blank" className="self-start" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default WhitePaper;
